import React, { useEffect, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { X, Download, ExternalLink, FileText, Mail } from 'lucide-react'
import { useCVModal } from '../contexts/CVModalContext'
import { apiUrl } from '../lib/api'

const CVModal: React.FC = () => {
  const { isOpen, closeModal } = useCVModal()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        return
      }

      if (e.key === 'Tab') {
        const panel = panelRef.current
        if (!panel) return
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
          )
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    closeRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, closeModal])

  if (!isOpen) return null

  const downloadUrl = apiUrl('/api/cv/download')

  const handleDownload = async () => {
    try {
      const response = await fetch(downloadUrl)
      if (!response.ok) throw new Error('Not found')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'Ezzddinne-Mhamdi-CV.pdf'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error("The PDF isn't generated yet — email me and I'll send it over.")
    }
  }

  const handleViewExternal = () => {
    window.open(downloadUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cv-modal-title"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm animate-fade-in"
        onClick={closeModal}
      />

      <div
        ref={panelRef}
        className="relative w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-white/10 shadow-2xl animate-slide-up"
      >
        <div className="flex items-center justify-between gap-4 p-6 border-b border-neutral-200 dark:border-white/6">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary-500/10 text-primary-500 flex-shrink-0">
              <FileText size={20} />
            </span>
            <div className="min-w-0">
              <h2
                id="cv-modal-title"
                className="font-display text-lg font-semibold truncate"
              >
                CV — Ezzddinne Mhamdi
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Junior Platform Engineer · Cloud & Kubernetes
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              className="btn-primary !py-2.5 !px-4 text-sm"
            >
              <Download size={15} />
              Download
            </button>
            <button
              onClick={handleViewExternal}
              className="btn-secondary !py-2.5 !px-3 text-sm"
              aria-label="Open CV in a new tab"
            >
              <ExternalLink size={15} />
            </button>
            <button
              ref={closeRef}
              onClick={closeModal}
              aria-label="Close CV dialog"
              className="p-2 rounded-lg text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
            >
              <X size={19} />
            </button>
          </div>
        </div>

        <div className="p-6 sm:p-8 overflow-y-auto">
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            This contact sheet summarizes the profile on this site. The full PDF
            is generated during release builds — if it isn't available yet, use
            the download or the button above and I'll send the latest version
            directly.
          </p>

          <div className="mt-6 space-y-3">
            {[
              ['Role', 'Junior Platform Engineer'],
              ['Email', 'ezzddinne.mhamdi@outlook.com'],
              ['Phone', '+216 50 119 340'],
              ['Location', 'Tunisia, North Africa'],
              ['Focus', 'Cloud · Kubernetes · Terraform · CI/CD · GitOps'],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-start justify-between gap-4 py-3 px-4 rounded-lg bg-neutral-50 dark:bg-white/[0.03]"
              >
                <span className="mono-label !text-[10px] pt-0.5">{label}</span>
                <span className="text-sm font-medium text-right">{value}</span>
              </div>
            ))}
          </div>

          <a
            href="mailto:ezzddinne.mhamdi@outlook.com?subject=CV%20request"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            <Mail size={15} />
            Request the full CV by email
          </a>
        </div>
      </div>
    </div>
  )
}

export default CVModal